import type {
  PaginatedResponse,
  ProspectRecord,
  ProspectListParams,
  ApprovalItem,
  ApprovalDetail,
  ReplyItem,
  ReplyListParams,
  ReplyDetail,
  HandoffBrief,
  OpportunityItem,
  SuppressionRecord,
  NewSuppression,
  OverviewMetrics,
  CostMetrics,
  Settings,
  ScoringWeights,
  ICPConfig,
  Thresholds,
} from '../types';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
const API_SECRET = import.meta.env.VITE_API_SECRET as string | undefined;

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
}

function transformKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(v => transformKeys(v));
  } else if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    for (const key of Object.keys(obj)) {
      const camelKey = toCamelCase(key);
      newObj[camelKey] = transformKeys(obj[key]);
    }
    return newObj;
  }
  return obj;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  const url = `${API_BASE}${path}`;
  const start = performance.now();

  try {
    const res = await fetch(url, {
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(API_SECRET ? { Authorization: `Bearer ${API_SECRET}` } : {}),
        ...(init?.headers ?? {}),
      },
    });

    const latencyMs = Math.round(performance.now() - start);

    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text();
      }
      console.error('[api]', { path, status: res.status, latencyMs, body });
      throw new ApiError(res.status, `HTTP ${res.status}: ${res.statusText}`, body);
    }

    if (res.status === 204) return undefined as T;

    const data = await res.json();
    console.debug('[api]', { path, status: res.status, latencyMs });
    return transformKeys(data) as T;
  } catch (err) {
    const latencyMs = Math.round(performance.now() - start);
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(408, `Request timeout after 30s: ${path}`);
    }
    console.error('[api] network error', { path, latencyMs, err });
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      q.set(k, String(v));
    }
  }
  const str = q.toString();
  return str ? `?${str}` : '';
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const api = {
  // ── Prospects ────────────────────────────────────────────────────────────

  listProspects(params?: ProspectListParams): Promise<PaginatedResponse<ProspectRecord>> {
    const q = buildQuery((params ?? {}) as Record<string, unknown>);
    return apiFetch<PaginatedResponse<ProspectRecord>>(`/prospects${q}`);
  },

  getProspect(id: string): Promise<ProspectRecord> {
    return apiFetch<ProspectRecord>(`/prospects/${encodeURIComponent(id)}`);
  },

  // ── Approval Queue ────────────────────────────────────────────────────────
  
  listPendingApprovals(): Promise<ApprovalItem[]> {
    return apiFetch<ApprovalItem[]>('/approval');
  },

  getApproval(id: string): Promise<ApprovalDetail> {
    return apiFetch<ApprovalDetail>(`/approval/${encodeURIComponent(id)}`);
  },

  approveMessage(id: string, notes?: string): Promise<void> {
    return apiFetch<void>(`/approval/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  rejectMessage(id: string, reason: string): Promise<void> {
    return apiFetch<void>(`/approval/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  editMessage(id: string, subject: string, body: string): Promise<void> {
    return apiFetch<void>(`/approval/${encodeURIComponent(id)}/edit`, {
      method: 'POST',
      body: JSON.stringify({ subject, body }),
    });
  },

  regenerateMessage(id: string): Promise<void> {
    return apiFetch<void>(`/approval/${encodeURIComponent(id)}/regenerate`, {
      method: 'POST',
    });
  },

  suppressContact(id: string): Promise<void> {
    return apiFetch<void>(`/approval/${encodeURIComponent(id)}/suppress`, {
      method: 'POST',
    });
  },

  // ── Replies ───────────────────────────────────────────────────────────────

  listReplies(params?: ReplyListParams): Promise<PaginatedResponse<ReplyItem>> {
    const q = buildQuery((params ?? {}) as Record<string, unknown>);
    return apiFetch<PaginatedResponse<ReplyItem>>(`/replies${q}`);
  },

  getReplySummary(id: string): Promise<ReplyDetail> {
    return apiFetch<ReplyDetail>(`/replies/${encodeURIComponent(id)}`);
  },

  createHandoff(id: string): Promise<HandoffBrief> {
    return apiFetch<HandoffBrief>(`/replies/${encodeURIComponent(id)}/handoff`, {
      method: 'POST',
    });
  },

  markReplyReviewed(id: string): Promise<void> {
    return apiFetch<void>(`/replies/${encodeURIComponent(id)}/reviewed`, {
      method: 'POST',
    });
  },

  // ── Opportunities ─────────────────────────────────────────────────────────

  listOpportunities(): Promise<OpportunityItem[]> {
    return apiFetch<OpportunityItem[]>('/opportunities');
  },

  createOpportunityHandoff(id: string): Promise<HandoffBrief> {
    return apiFetch<HandoffBrief>(`/opportunities/${encodeURIComponent(id)}/handoff`, {
      method: 'POST',
    });
  },

  // ── Suppression ───────────────────────────────────────────────────────────

  listSuppression(): Promise<SuppressionRecord[]> {
    return apiFetch<SuppressionRecord[]>('/suppression');
  },

  addSuppression(entry: NewSuppression): Promise<void> {
    return apiFetch<void>('/suppression', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  removeSuppression(id: string): Promise<void> {
    return apiFetch<void>(`/suppression/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ── Metrics ───────────────────────────────────────────────────────────────

  getOverview(): Promise<OverviewMetrics> {
    return apiFetch<OverviewMetrics>('/metrics/overview');
  },

  getCosts(): Promise<CostMetrics> {
    return apiFetch<CostMetrics>('/metrics/costs');
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  
  getSettings(): Promise<Settings> {
    return apiFetch<Settings>('/settings');
  },

  updateScoringWeights(weights: ScoringWeights): Promise<void> {
    return apiFetch<void>('/settings/scoring', {
      method: 'PUT',
      body: JSON.stringify(weights),
    });
  },

  updateICPConfig(config: ICPConfig): Promise<void> {
    return apiFetch<void>('/settings/icp', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  updateThresholds(thresholds: Thresholds): Promise<void> {
    return apiFetch<void>('/settings/thresholds', {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    });
  },
};

export { ApiError };
